import type { PlanId } from "./plans.ts";
import type { IdentityPlane, MembershipRole, WorkspaceKind } from "./scope.ts";
import type { RunPauseReason, RunState } from "./run-state.ts";
import type {
  AgentId,
  ApprovalId,
  BranchId,
  ConversationId,
  CredentialId,
  FileId,
  GoalId,
  MemoryId,
  MessageId,
  OrganizationId,
  RunId,
  ScheduleId,
  ToolCallId,
  UserId,
  WorkspaceId,
} from "./ids.ts";

/** ISO-8601 timestamp string, always UTC. */
export type Timestamp = string;

export interface Base {
  readonly createdAt: Timestamp;
  readonly updatedAt: Timestamp;
}

export interface User extends Base {
  readonly userId: UserId;
  readonly displayName: string | null;
  readonly primaryEmail: string | null;
}

/**
 * A login identity. A single person may have one personal identity and many
 * work identities; they are linked for account switching but their data is
 * never merged (prompt.md §6.2).
 */
export interface Identity extends Base {
  readonly identityId: string;
  readonly userId: UserId;
  readonly plane: IdentityPlane;
  /**
   * "local" is the dev-only identity provider (specs/DESIGN-local-signin.md):
   * real rows, same flow, only the upstream assertion is local. The OAuth
   * machinery keeps its own closed union in @dayvise/auth and never sees it.
   */
  readonly provider: "google" | "github" | "workos" | "local";
  /** The provider's own stable subject. Sign-in matches on this, never on email. */
  readonly providerUserId: string;
  readonly email: string | null;
  readonly organizationId: OrganizationId | null;
}

export interface Organization extends Base {
  readonly organizationId: OrganizationId;
  readonly name: string;
  readonly plan: PlanId;
  readonly workosOrganizationId: string | null;
}

export interface Workspace extends Base {
  readonly workspaceId: WorkspaceId;
  readonly kind: WorkspaceKind;
  readonly name: string;
  readonly ownerUserId: UserId;
  readonly organizationId: OrganizationId | null;
  readonly plan: PlanId;
  readonly storageRouteId: string | null;
  readonly retentionDays?: number | null;
}

export interface Membership extends Base {
  readonly membershipId: string;
  readonly workspaceId: WorkspaceId;
  readonly userId: UserId;
  readonly role: MembershipRole;
}

/**
 * A connected MCP server (prompt.md §26).
 *
 * The field names follow the manifest format Hermes ships in
 * `optional-mcps/<name>/manifest.yaml`, so an approved catalogue entry can be
 * imported as-is rather than hand-translated.
 */
export interface McpServer extends Base {
  readonly mcpServerId: string;
  readonly workspaceId: WorkspaceId;
  /** Null means the whole workspace shares it; set means it is one person's. */
  readonly ownerUserId: UserId | null;
  readonly name: string;
  readonly transport: McpTransport;
  readonly url: string | null;
  /** `oauth`, `bearer` or `none`. */
  readonly authType: McpAuthType;
  /** Resolved secret, when the transport needs one. */
  readonly credentialId: string | null;
  /** Which of the server's tools to offer. Null means all of them. */
  readonly toolFilter: McpToolFilter | null;
  /** The server's tools as last discovered, or null if it has never answered. */
  readonly tools: readonly McpToolDescriptor[] | null;
  readonly sourceUrl: string | null;
  readonly shared: boolean;
  readonly enabled: boolean;
  readonly protocolVersion: string | null;
  readonly health: McpHealth | null;
}

export type McpTransport = "http" | "stdio";
export type McpAuthType = "oauth" | "bearer" | "none";
export type McpHealth = "unknown" | "healthy" | "unreachable" | "unauthorized";

/**
 * Tool selection for a server.
 *
 * Cloudflare's remote MCP exposes roughly 1,900 tools. Offering all of them costs
 * more context than most conversations have, so a server is expected to declare a
 * subset — and progressive disclosure covers the rest.
 */
export interface McpToolFilter {
  readonly defaultEnabled?: readonly string[];
  readonly defaultExcluded?: readonly string[];
}

/**
 * One tool a remote MCP server exposes.
 *
 * The shape is the MCP `tools/list` entry, kept as the server sent it so an
 * `inputSchema` can be handed to the model without translation.
 */
export interface McpToolDescriptor {
  readonly name: string;
  readonly description: string | null;
  readonly inputSchema: unknown;
}

/**
 * A skill (prompt.md §27).
 *
 * The body is Markdown with a YAML frontmatter block, matching Hermes'
 * `SKILL.md`. Storing the frontmatter verbatim means a skill authored elsewhere
 * keeps its fields instead of losing whatever this schema did not anticipate.
 */
export interface Skill extends Base {
  readonly skillId: string;
  /** Imported profile overrides belong to one agent; null/absent means the workspace library. */
  readonly agentId?: AgentId | null;
  readonly workspaceId: WorkspaceId;
  readonly ownerUserId: UserId | null;
  readonly name: string;
  readonly version: string;
  readonly visibility: SkillVisibility;
  readonly frontmatter: Record<string, unknown> | null;
  readonly platforms: readonly string[];
  readonly tags: readonly string[];
  readonly category: string | null;
  readonly source: SkillSource;
  readonly requiredTools: readonly string[];
  readonly requiredSecrets: readonly string[];
  /** The skill's Markdown body, frontmatter removed. */
  readonly instructions: string;
  readonly compatibility: Record<string, unknown> | null;
  /** Set while archived: history and versions stay, but runs are no longer offered the skill. */
  readonly archivedAt?: string | null;
}

export type SkillVisibility = "private" | "workspace" | "organization";
export type SkillSource = "builtin" | "optional" | "agent";

export const REASONING_EFFORTS = ["none", "minimal", "low", "medium", "high", "xhigh", "max", "ultra"] as const;
export type ReasoningEffort = typeof REASONING_EFFORTS[number];
export function isReasoningEffort(value: unknown): value is ReasoningEffort {
  return typeof value === "string" && (REASONING_EFFORTS as readonly string[]).includes(value);
}

export interface Agent extends Base {
  readonly agentId: AgentId;
  readonly workspaceId: WorkspaceId;
  readonly ownerUserId: UserId;
  readonly name: string;
  readonly description: string | null;
  readonly systemPrompt: string | null;
  /** Named overlay; the manual systemPrompt is retained independently. */
  readonly personality?: string | null;
  readonly personalities?: import("./personality.ts").Personalities | null;
  readonly modelProvider: string | null;
  readonly modelName: string | null;
  readonly modelReasoningEffort?: ReasoningEffort | null;
  readonly shared: boolean;
  readonly archivedAt: Timestamp | null;
}

export interface Conversation extends Base {
  readonly conversationId: ConversationId;
  readonly workspaceId: WorkspaceId;
  readonly agentId: AgentId;
  readonly ownerUserId: UserId;
  /** Private conversations are invisible to admins apart from metadata. */
  readonly visibility: "private" | "workspace";
  readonly title: string | null;
  readonly defaultBranchId: BranchId;
  readonly messageCount: number;
  readonly deletedAt: Timestamp | null;
}

/**
 * A conversation is a DAG of branches. Parallel runs each append to their own
 * branch, so two concurrent runs can never corrupt one linear history
 * (prompt.md §19).
 */
export interface ConversationBranch extends Base {
  readonly branchId: BranchId;
  readonly workspaceId: WorkspaceId;
  readonly conversationId: ConversationId;
  /** Optional user label for an explicit branch. */
  readonly name?: string | null;
  readonly parentBranchId: BranchId | null;
  readonly parentMessageId: MessageId | null;
  readonly snapshotVersion: number;
  readonly status: "open" | "merged" | "abandoned";
  readonly ownerRunId: RunId | null;
}

export type MessageRole = "user" | "assistant" | "system" | "developer" | "tool";

export interface MessagePartText {
  readonly type: "text";
  readonly text: string;
  /** Editor-composed prose must survive cold starts without command expansion. */
  readonly interpretSlash?: boolean;
}
export interface MessagePartImage {
  readonly type: "image";
  readonly url: string;
  readonly mimeType: string;
}
export interface MessagePartFile {
  readonly type: "file";
  readonly fileId: FileId;
  readonly name: string;
  readonly mimeType: string;
}
export interface MessagePartToolCall {
  readonly type: "tool_call";
  readonly toolCallId: ToolCallId;
  readonly toolId: string;
  readonly arguments: unknown;
}
export interface MessagePartToolResult {
  readonly type: "tool_result";
  readonly toolCallId: ToolCallId;
  readonly ok: boolean;
  readonly content: unknown;
}
export interface MessagePartReasoning {
  readonly type: "reasoning";
  readonly text: string;
}

/**
 * A sealed user secret (prompt.md §13; Hermes vault pattern, Dayvise-adapted).
 *
 * The value is AES-256-GCM sealed at send time and stored inside the part:
 * no new table, so retention deletes secrets together with their messages.
 * The model only ever sees `secretId` + `label` via the history placeholder;
 * the control-plane tool gateway resolves `{{secret:<secretId>}}` references
 * in tool arguments server-side at execution time.
 */
export interface SealedEnvelope {
  readonly ciphertext: string;
  readonly encryptedDek: string;
  readonly iv: string;
  readonly keyVersion: number;
}

/**
 * What a sealed message secret is bound to (prompt.md §13).
 *
 * The binding is both metadata and authenticated data: it is the AAD that
 * authenticates the envelope, so a ciphertext moved to another tenant,
 * conversation, branch, handle or expiry window fails to open instead of
 * resolving.
 */
export interface MessageSecretBinding {
  readonly secretId: string;
  readonly conversationId: string;
  readonly branchId: string;
  readonly expiresAt: string;
  readonly use: "task";
}

export interface MessagePartSecret extends MessageSecretBinding {
  readonly type: "secret";
  /** Opaque handle (`sec_<32 hex>`), safe to show to the model and in logs. */
  readonly secretId: string;
  /** User-supplied label, e.g. "db password". Model-visible, never the value. */
  readonly label: string;
  readonly sealed: SealedEnvelope;
  /**
   * Set only when the person explicitly asked to save a reusable credential
   * alongside the task-scoped copy; the reusable copy lives in the credential
   * store under this id and is revoked there, never here.
   */
  readonly savedCredentialId?: string | null;
}

export type MessagePart =
  | MessagePartText
  | MessagePartImage
  | MessagePartFile
  | MessagePartToolCall
  | MessagePartToolResult
  | MessagePartReasoning
  | MessagePartSecret;

/** Provider-neutral message. Provider internals are never authoritative here. */
export interface Message {
  readonly messageId: MessageId;
  readonly workspaceId: WorkspaceId;
  readonly conversationId: ConversationId;
  readonly branchId: BranchId;
  readonly runId: RunId | null;
  readonly role: MessageRole;
  readonly parts: readonly MessagePart[];
  readonly sequence: number;
  /**
   * Claim key that produced this message (e16s08): the durable link between
   * reserved intent and its effect. Null for assistant/system rows and rows
   * written before markers existed.
   */
  readonly idempotencyKey?: string | null;
  readonly createdAt: Timestamp;
}

export type RunTrigger =
  | "user_message"
  | "schedule"
  | "webhook"
  | "api"
  | "agent_message"
  | "resume"
  | "retry";

export interface RunCheckpoint {
  readonly checkpointId: string;
  readonly runId: RunId;
  readonly sequence: number;
  readonly state: RunState;
  readonly conversationSnapshotVersion: number;
  readonly pendingToolCall: { toolCallId: ToolCallId; toolId: string; arguments: unknown } | null;
  readonly pendingModelCall: unknown | null;
  readonly workspaceManifest: readonly WorkspaceFileEntry[];
  readonly outstandingApprovals: readonly ApprovalId[];
  readonly usage: RunUsageTotals;
  readonly resumeConditions: readonly string[];
  readonly promptVersion: string;
  readonly createdAt: Timestamp;
}

export interface RunUsageTotals {
  readonly inputTokens: number;
  readonly outputTokens: number;
  readonly toolCalls: number;
  readonly wallClockMs: number;
  readonly cpuMs: number;
  readonly containerMs: number;
}

/** Provider-supported processing tier, chosen alongside the model (prompt.md §78.3). */
export type ProcessingMode = "default" | "flex" | "priority";

export interface Run extends Base {
  readonly runId: RunId;
  readonly workspaceId: WorkspaceId;
  readonly agentId: AgentId;
  readonly conversationId: ConversationId;
  readonly branchId: BranchId;
  readonly ownerUserId: UserId;
  readonly trigger: RunTrigger;
  readonly state: RunState;
  /**
   * Claim key that created this run (e16s08): links reserved intent to its
   * effect so a started claim is retained and resumed, never released.
   * Undefined for runs created outside a claim.
   */
  readonly idempotencyKey?: string | null;
  /**
   * Op revision of the last successful conditional transition (e16s08).
   * Undefined for rows never transitioned through the atomic primitive.
   */
  readonly revision?: string | null;
  readonly pauseReason: RunPauseReason | null;
  readonly attempt: number;
  readonly maxAttempts: number;
  readonly checkpointId: string | null;
  /** Pinned when the run is created; null only for runs predating this field. */
  /** Workspace-relative directory frozen when this run is created. */
  readonly workingDirectory?: string;
  readonly selectedModelProvider?: string | null;
  readonly selectedModelName?: string | null;
  readonly selectedReasoningEffort?: ReasoningEffort | null;
  /** Frozen with the run: a retry or resume reuses the recorded mode. */
  readonly selectedProcessingMode?: ProcessingMode | null;
  /** System prompt captured at creation; nullable when intentionally absent. */
  readonly systemPromptSnapshot?: string | null;
  /** False or absent identifies legacy runs that predate prompt snapshots. */
  readonly systemPromptSnapshotRecorded?: boolean;
  readonly providerFallbackUsed: string | null;
  readonly promptVersion: string;
  readonly usage: RunUsageTotals;
  readonly error: string | null;
  readonly startedAt: Timestamp | null;
  readonly finishedAt: Timestamp | null;
}

export interface WorkspaceFileEntry {
  readonly fileId: FileId;
  readonly path: string;
  readonly version: number;
  readonly hash: string;
  readonly mimeType: string;
  readonly size: number;
  readonly storageProvider: "structured" | "blob";
  readonly blobObjectId: string | null;
}

export interface FileRecord extends Base {
  readonly fileId: FileId;
  readonly workspaceId: WorkspaceId;
  readonly ownerUserId: UserId;
  readonly conversationId: ConversationId | null;
  readonly runId: RunId | null;
  readonly path: string;
  readonly version: number;
  readonly hash: string;
  readonly mimeType: string;
  readonly size: number;
  readonly storageProvider: "structured" | "blob";
  readonly blobObjectId: string | null;
  readonly parentFileId: FileId | null;
  readonly deletedAt: Timestamp | null;
}

export type MemoryScope = "private" | "shared";

export interface MemoryRecord extends Base {
  readonly memoryId: MemoryId;
  readonly workspaceId: WorkspaceId;
  readonly scope: MemoryScope;
  /** Private memory owner. `null` for shared memory. */
  readonly ownerUserId: UserId | null;
  readonly agentId: AgentId | null;
  readonly content: string;
  readonly sourceConversationId: ConversationId | null;
  readonly sourceMessageId: MessageId | null;
  readonly confidence: number;
  readonly userConfirmed: boolean;
  readonly retentionClass: "ephemeral" | "standard" | "long";
  /**
   * Policy generation under which this memory entered retention enforcement.
   * `0` means it predates class retention and is grandfathered out of class-TTL
   * deletion; new memories carry the current `RETENTION_POLICY_VERSION`.
   */
  readonly retentionPolicyVersion: number;
  readonly sensitivity: "low" | "medium" | "high";
  readonly version: number;
  readonly promotedAt: Timestamp | null;
  readonly promotedByUserId: UserId | null;
  readonly deletedAt: Timestamp | null;
}

export type ScheduleOverlapPolicy = "queue" | "skip" | "parallel";
export type ScheduleMissedRunPolicy = "skip" | "run_once" | "run_all";

export interface Schedule extends Base {
  readonly scheduleId: ScheduleId;
  readonly workspaceId: WorkspaceId;
  readonly agentId: AgentId;
  readonly ownerUserId: UserId;
  readonly title: string;
  readonly prompt: string;
  readonly cronExpression: string | null;
  readonly runAt: Timestamp | null;
  readonly timezone: string;
  readonly enabled: boolean;
  readonly overlapPolicy: ScheduleOverlapPolicy;
  readonly missedRunPolicy: ScheduleMissedRunPolicy;
  readonly providerOverride: string | null;
  readonly failureBackoffSeconds: number;
  readonly notifyOnCompletion: boolean;
  readonly lastRunAt: Timestamp | null;
  readonly lastRunId: RunId | null;
  readonly nextRunAt: Timestamp | null;
  readonly consecutiveFailures: number;
  /**
   * Monotonic CAS revision (e16s08): every mutation goes through a
   * revision-guarded single-statement update. Undefined for rows written
   * before revisions existed; NULL counts as 0 in guards.
   */
  readonly revision?: number | null;
}

export type GoalStatus = "active" | "paused" | "completed" | "cleared";
export type GoalTaskStatus = "pending" | "in_progress" | "completed" | "cancelled";

/** One hierarchical goal task (Hermes todo semantics, conversation-scoped). */
export interface GoalTask {
  readonly id: string;
  readonly content: string;
  readonly status: GoalTaskStatus;
  readonly parent: string | null;
}

/**
 * A persistent conversation goal (prompt.md §14, Hermes `run_goals` semantics):
 * the objective survives across runs, and while `loopEnabled` the scheduler
 * starts continuation runs until the goal completes.
 */
export interface Goal extends Base {
  readonly goalId: GoalId;
  readonly workspaceId: WorkspaceId;
  readonly conversationId: ConversationId;
  readonly agentId: AgentId;
  readonly ownerUserId: UserId;
  readonly objective: string;
  readonly status: GoalStatus;
  readonly tasks: readonly GoalTask[];
  readonly nextAction: string | null;
  readonly loopEnabled: boolean;
  readonly loopIntervalMinutes: number;
  readonly loopPrompt: string | null;
  readonly iterations: number;
  readonly lastTickAt: Timestamp | null;
  readonly nextTickAt: Timestamp | null;
  readonly lastRunId: RunId | null;
  readonly consecutiveFailures: number;
  readonly revision?: number | null;
}

export interface Credential extends Base {
  readonly credentialId: CredentialId;
  readonly workspaceId: WorkspaceId;
  readonly ownerUserId: UserId | null;
  readonly organizationId: OrganizationId | null;
  readonly kind: "ai_provider" | "byo_database" | "byo_blob" | "mcp_server" | "secret";
  readonly provider: string;
  readonly label: string;
  readonly scope: "personal" | "organization";
  readonly keyVersion: number;
  readonly fingerprint: string;
  readonly revokedAt: Timestamp | null;
  readonly lastTestedAt: Timestamp | null;
  readonly lastTestOk: boolean | null;
}

export type ApprovalStatus = "pending" | "approved" | "rejected" | "expired";

export interface Approval extends Base {
  readonly approvalId: ApprovalId;
  readonly workspaceId: WorkspaceId;
  readonly runId: RunId;
  readonly toolCallId: ToolCallId;
  readonly toolId: string;
  readonly arguments: unknown;
  readonly riskLevel: "low" | "medium" | "high" | "critical";
  readonly status: ApprovalStatus;
  readonly requestedByPrincipalId: string;
  readonly decidedByUserId: UserId | null;
  readonly decidedAt: Timestamp | null;
  readonly note: string | null;
}

export interface StorageRoute {
  readonly routeId: string;
  readonly workspaceId: WorkspaceId;
  readonly structured: "d1_shared" | "postgres_byo";
  readonly blob: "r2_managed" | "s3_byo";
  readonly shardKey: string | null;
  readonly health: "healthy" | "degraded" | "unavailable";
  readonly lastCheckedAt: Timestamp | null;
}

export interface UsageRecord {
  readonly usageRecordId: string;
  /** Runtime snapshots include earlier usage. Persist only the unrecorded delta. */
  readonly cumulative?: boolean;
  readonly workspaceId: WorkspaceId;
  readonly runId: RunId | null;
  readonly meter:
    | "workers_ai_tokens"
    | "byok_tokens"
    | "container_ms"
    | "cpu_ms"
    | "wall_clock_ms"
    | "storage_bytes"
    | "bandwidth_bytes"
    | "queue_events"
    | "tool_calls"
    | "external_calls";
  readonly quantity: number;
  readonly unit: string;
  readonly billable: boolean;
  readonly recordedAt: Timestamp;
}

export interface AuditEvent {
  readonly auditEventId: string;
  readonly workspaceId: WorkspaceId;
  readonly organizationId: OrganizationId | null;
  readonly actorPrincipalId: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string | null;
  readonly metadata: Record<string, string | number | boolean | null>;
  readonly createdAt: Timestamp;
}
